import {head, isNil, last} from 'ramda';

import type {RoutePoint} from './getRouteBounds';

// Each pass cuts every corner once, two passes round the GPS zigzag without moving the street.
const SMOOTHING_PASSES = 2;
const CUT_RATIO = 0.25;

const cutCorners = (path: RoutePoint[]) => {
  const first = head(path);
  const final = last(path);
  if (path.length < 3 || isNil(first) || isNil(final)) {
    return path;
  }

  const cut = path.slice(0, -1).flatMap((point, index) => {
    const next = path[index + 1] ?? point;
    return [
      {
        lat: point.lat + (next.lat - point.lat) * CUT_RATIO,
        lng: point.lng + (next.lng - point.lng) * CUT_RATIO,
      },
      {
        lat: point.lat + (next.lat - point.lat) * (1 - CUT_RATIO),
        lng: point.lng + (next.lng - point.lng) * (1 - CUT_RATIO),
      },
    ];
  });

  return [first, ...cut, final];
};

/**
 * Rounds the corners of a recorded route (Chaikin's algorithm), so the GPS zigzag reads as a
 * driven line. The start and finish stay where they were recorded.
 */
export const smoothRoute = (path: RoutePoint[]) =>
  Array.from({length: SMOOTHING_PASSES}).reduce<RoutePoint[]>(cutCorners, path);
